import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { uniqueChannelName } from '../../lib/realtime';

export interface Reaction {
  id: string;
  message_id: string;
  user_id: string;
  emoji: string;
  user_name: string;
  created_at: string;
}

// Cache user names to avoid repeated lookups
const userNameCache = new Map<string, string>();

async function fetchUserName(userId: string): Promise<string> {
  const cached = userNameCache.get(userId);
  if (cached) return cached;

  const { data, error } = await supabase
    .from('users')
    .select('name')
    .eq('id', userId)
    .single();

  const name = !error && data?.name ? data.name : 'Unknown';
  userNameCache.set(userId, name);
  return name;
}

export function useChatReactions(messageIds: string[]) {
  const { user } = useUser();
  const [reactions, setReactions] = useState<Map<string, Reaction[]>>(new Map());
  const prevIdsRef = useRef<string>('');

  // Fetch reactions for a batch of message IDs
  const fetchReactions = useCallback(async (ids: string[]) => {
    if (ids.length === 0) {
      setReactions(new Map());
      return;
    }

    try {
      const { data, error } = await supabase
        .from('chat_reactions')
        .select('*')
        .in('message_id', ids);

      if (error) throw error;

      // Fetch all unique user names
      const userIds = [...new Set((data || []).map(r => r.user_id))];
      await Promise.all(userIds.map(id => fetchUserName(id)));

      const reactionMap = new Map<string, Reaction[]>();

      // Initialize empty arrays for all requested message IDs
      for (const id of ids) {
        reactionMap.set(id, []);
      }

      for (const row of data || []) {
        const reaction: Reaction = {
          id: row.id,
          message_id: row.message_id,
          user_id: row.user_id,
          emoji: row.emoji,
          user_name: userNameCache.get(row.user_id) || 'Unknown',
          created_at: row.created_at,
        };

        const existing = reactionMap.get(row.message_id) || [];
        existing.push(reaction);
        reactionMap.set(row.message_id, existing);
      }

      setReactions(reactionMap);
    } catch (err) {
      console.error('Error fetching reactions:', err);
    }
  }, []);

  // Refetch when messageIds change
  useEffect(() => {
    const idsKey = messageIds.join(',');
    if (idsKey === prevIdsRef.current) return;
    prevIdsRef.current = idsKey;

    fetchReactions(messageIds);
  }, [messageIds, fetchReactions]);

  // Toggle a reaction (add if not present, remove if already exists)
  const addReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user) return;

      // Check if user already reacted with this emoji
      const messageReactions = reactions.get(messageId) || [];
      const existing = messageReactions.find(
        r => r.user_id === user.id && r.emoji === emoji
      );

      if (existing) {
        // Remove it (toggle off)
        await removeReaction(messageId, emoji);
        return;
      }

      // Optimistic insert
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const optimisticReaction: Reaction = {
        id: tempId,
        message_id: messageId,
        user_id: user.id,
        emoji,
        user_name: user.name || 'Unknown',
        created_at: new Date().toISOString(),
      };

      setReactions(prev => {
        const next = new Map(prev);
        const existing = next.get(messageId) || [];
        next.set(messageId, [...existing, optimisticReaction]);
        return next;
      });

      try {
        const { error } = await supabase
          .from('chat_reactions')
          .insert({
            message_id: messageId,
            user_id: user.id,
            emoji,
          });

        if (error) throw error;

        // Cache user name for current user
        userNameCache.set(user.id, user.name || 'Unknown');
      } catch (err) {
        console.error('Error adding reaction:', err);
        // Revert optimistic update
        setReactions(prev => {
          const next = new Map(prev);
          const list = (next.get(messageId) || []).filter(r => r.id !== tempId);
          next.set(messageId, list);
          return next;
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, reactions]
  );

  // Remove a reaction
  const removeReaction = useCallback(
    async (messageId: string, emoji: string) => {
      if (!user) return;

      // Optimistic removal
      let removedReaction: Reaction | undefined;
      setReactions(prev => {
        const next = new Map(prev);
        const list = next.get(messageId) || [];
        removedReaction = list.find(r => r.user_id === user.id && r.emoji === emoji);
        next.set(
          messageId,
          list.filter(r => !(r.user_id === user.id && r.emoji === emoji))
        );
        return next;
      });

      try {
        const { error } = await supabase
          .from('chat_reactions')
          .delete()
          .eq('message_id', messageId)
          .eq('user_id', user.id)
          .eq('emoji', emoji);

        if (error) throw error;
      } catch (err) {
        console.error('Error removing reaction:', err);
        // Revert optimistic removal
        if (removedReaction) {
          const reactionToRestore = removedReaction;
          setReactions(prev => {
            const next = new Map(prev);
            const list = next.get(messageId) || [];
            next.set(messageId, [...list, reactionToRestore]);
            return next;
          });
        }
      }
    },
    [user]
  );

  // Realtime subscription for INSERT and DELETE on chat_reactions
  useEffect(() => {
    if (messageIds.length === 0) return;

    const subscription = supabase
      .channel(uniqueChannelName('chat-reactions-realtime'))
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_reactions',
        },
        async (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const newRow = payload.new as any;

          // Only process if it's for one of our tracked messages
          if (!messageIds.includes(newRow.message_id)) return;

          // Skip if we already have this reaction (e.g. from optimistic update)
          setReactions(prev => {
            const list = prev.get(newRow.message_id) || [];
            if (list.some(r => r.id === newRow.id)) return prev;
            // Temp-id duplicates (our optimistic insert) are reconciled in the async callback below.
            return prev;
          });

          const userName = await fetchUserName(newRow.user_id);
          const reaction: Reaction = {
            id: newRow.id,
            message_id: newRow.message_id,
            user_id: newRow.user_id,
            emoji: newRow.emoji,
            user_name: userName,
            created_at: newRow.created_at,
          };

          setReactions(prev => {
            const next = new Map(prev);
            const list = next.get(newRow.message_id) || [];

            // Replace temp version if exists, otherwise add if not duplicate
            const tempIndex = list.findIndex(
              r => r.id.startsWith('temp-') && r.user_id === newRow.user_id && r.emoji === newRow.emoji
            );
            if (tempIndex >= 0) {
              const updated = [...list];
              updated[tempIndex] = reaction;
              next.set(newRow.message_id, updated);
            } else if (!list.some(r => r.id === newRow.id)) {
              next.set(newRow.message_id, [...list, reaction]);
            }

            return next;
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'chat_reactions',
        },
        (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const oldRow = payload.old as any;
          if (!oldRow?.id) return;

          setReactions(prev => {
            const next = new Map(prev);
            // We need to search all entries since we might not know the message_id from DELETE
            for (const [msgId, list] of next.entries()) {
              const filtered = list.filter(r => r.id !== oldRow.id);
              if (filtered.length !== list.length) {
                next.set(msgId, filtered);
                break;
              }
            }
            return next;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [messageIds]);

  return { reactions, addReaction, removeReaction };
}
